import React, { forwardRef } from 'react';
import { UIModuleTypeForm } from '../../UIModuleForm';
import {
  NewPartnerLandingUiModuleBody,
  NewPartnerLandingUiModuleFaqQuestionsBody,
  NewPartnerLandingUiModuleStepsToRedeemBody
} from '../../../../../API';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { FormBody, FormField, SectionHeader } from '../../styles';
import { useLocales, useTheme } from '../../../../../hooks';
import { LokaliseAutocomplete } from '../../../../shared/LokaliseAutocomplete';
import { Box, Button, IconButton, Stack } from '@mui/material';
import { LocaleKeys } from '../../../../../locales/i18n';
import { AssetTypes, AssetTypesEnum } from '../../../../../utils/assetTypes';
import { markAsRequired } from '../../../../../utils/formHelpers';
import AssetBrowser from '../../../../Assets/AssetBrowser';
import { Add, Delete } from '@mui/icons-material';
import { Sortable } from '../../../../shared/Sortable';
import generateId from '../../../../../utils/generateId';
import { LokaliseField } from '../../../../shared/LokaliseFields';
import { newPartnerLandingFormTestIds } from '../../../../shared/TestsIds';

const NewPartnerLandingForm = forwardRef<UIModuleTypeForm<NewPartnerLandingUiModuleBody>>(() => {
  const { control, watch } = useFormContext<NewPartnerLandingUiModuleBody>();
  const { t } = useLocales();
  const { formControlColor } = useTheme();

  const {
    append: appendFaqQuestions,
    remove: removeFaqQuestions,
    fields: fieldsFaqQuestions,
    replace: replaceFaqQuestionsMethod
  } = useFieldArray({ control, name: 'faqQuestions' });

  const {
    append: appendStepsToRedeem,
    remove: removeStepsToRedeem,
    fields: fieldsStepsToRedeem,
    replace: replaceStepsToRedeemMethod
  } = useFieldArray({ control, name: 'stepsToRedeem' });

  const moduleId = watch('moduleId');

  const LokaliseFieldFaqQuestions = (
    name: keyof NewPartnerLandingUiModuleFaqQuestionsBody,
    position: number,
    required = false
  ): React.ReactElement => (
    <FormField>
      <Controller
        control={control}
        name={`faqQuestions.${position}.${name}`}
        render={({ field: { value, onChange } }) => (
          <LokaliseAutocomplete
            label={t(`layouts.new_partner_landing.fields.faqQuestions.${name}` as LocaleKeys)}
            name={name}
            value={value as string}
            onChange={onChange}
            required={required}
            data-testid={newPartnerLandingFormTestIds.formField(name)}
          />
        )}
      />
    </FormField>
  );

  const LokaliseFieldStepsToRedeem = (
    name: keyof NewPartnerLandingUiModuleStepsToRedeemBody,
    position: number,
    required = false
  ): React.ReactElement => (
    <FormField>
      <Controller
        control={control}
        name={`stepsToRedeem.${position}.${name}`}
        render={({ field: { value, onChange } }) => (
          <LokaliseAutocomplete
            label={t(`layouts.new_partner_landing.fields.stepsToRedeem.${name}` as LocaleKeys)}
            name={name}
            value={value as string}
            onChange={onChange}
            required={required}
            data-testid={newPartnerLandingFormTestIds.formField(name)}
          />
        )}
      />
    </FormField>
  );

  const AssetPicker = ({
    name,
    assetType,
    required = false
  }: {
    name: keyof NewPartnerLandingUiModuleBody;
    assetType: AssetTypesEnum;
    required?: boolean;
  }): JSX.Element => {
    const isRequired = (label: string) => (required ? markAsRequired(label) : label);
    return (
      <Controller
        control={control}
        name={name}
        rules={{ required }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <FormField>
            <AssetBrowser
              label={isRequired(t(`layouts.new_partner_landing.fields.${name}` as LocaleKeys))}
              assetId={moduleId || ''}
              assetType={assetType}
              value={value as string}
              data-testid={newPartnerLandingFormTestIds.formField(name)}
              hasError={!!error}
              onChange={onChange}
            />
          </FormField>
        )}
      />
    );
  };

  const onAddFaqQuestions = () => {
    appendFaqQuestions({
      id: generateId(),
      question: '',
      answer: ''
    });
  };

  const onAddStepsToRedeem = () => {
    appendStepsToRedeem({
      id: generateId(),
      title: '',
      body: ''
    });
  };

  return (
    <FormBody data-testid={newPartnerLandingFormTestIds.formBody}>
      <Box>
        <SectionHeader>{t('layouts.new_partner_landing.headers.header')}</SectionHeader>
        <AssetPicker name="vixLogo" assetType={AssetTypes.newPartnerLanding.vixLogo} required />
        <AssetPicker name="partnerLogo" assetType={AssetTypes.newPartnerLanding.partnerLogo} />
        <LokaliseField
          name="header"
          control={control}
          toTranslate="layouts.new_partner_landing.fields"
          dataTestId={newPartnerLandingFormTestIds.formField('header')}
          required
        />
        <LokaliseField
          name="subHeader"
          control={control}
          toTranslate="layouts.new_partner_landing.fields"
          dataTestId={newPartnerLandingFormTestIds.formField('subHeader')}
        />
        <LokaliseField
          name="description"
          control={control}
          toTranslate="layouts.new_partner_landing.fields"
          dataTestId={newPartnerLandingFormTestIds.formField('description')}
        />
        <div />
        <div>
          <Sortable
            list={fieldsStepsToRedeem}
            setList={replaceStepsToRedeemMethod}
            ghostClass="sortableGhost"
            dragClass="sortableDragDefault"
          >
            {fieldsStepsToRedeem.map((faq, i) => (
              <Stack
                key={faq.id}
                data-testid={newPartnerLandingFormTestIds.stepsToRedeem}
                gap={6}
                marginBottom={5}
                direction="row"
                alignItems="center"
              >
                {LokaliseFieldStepsToRedeem('title', i)}
                {LokaliseFieldStepsToRedeem('body', i)}
                <IconButton
                  title={t('layouts.new_partner_landing.actions.remove_steps_to_redeem')}
                  onClick={() => removeStepsToRedeem(i)}
                  sx={{ mt: 4 }}
                  data-testid={newPartnerLandingFormTestIds.stepsToRedeemRemoveButton}
                >
                  <Delete />
                </IconButton>
              </Stack>
            ))}
          </Sortable>
          <Button
            startIcon={<Add />}
            color={formControlColor}
            variant="outlined"
            onClick={onAddStepsToRedeem}
            data-testid={newPartnerLandingFormTestIds.stepsToRedeemAddButton}
          >
            {t('layouts.new_partner_landing.actions.add_steps_to_redeem')}
          </Button>
        </div>
      </Box>
      <Box>
        <SectionHeader>{t('layouts.new_partner_landing.headers.plan_picker')}</SectionHeader>
        <LokaliseField
          name="planName"
          control={control}
          toTranslate="layouts.new_partner_landing.fields"
          dataTestId={newPartnerLandingFormTestIds.formField('planName')}
          required
        />
        <LokaliseField
          name="planTier"
          control={control}
          toTranslate="layouts.new_partner_landing.fields"
          dataTestId={newPartnerLandingFormTestIds.formField('planTier')}
        />
        <LokaliseField
          name="planDescription"
          control={control}
          toTranslate="layouts.new_partner_landing.fields"
          dataTestId={newPartnerLandingFormTestIds.formField('planDescription')}
        />
        <LokaliseField
          name="planCost"
          control={control}
          toTranslate="layouts.new_partner_landing.fields"
          dataTestId={newPartnerLandingFormTestIds.formField('planCost')}
        />
        <LokaliseField
          name="viewBenefits"
          control={control}
          toTranslate="layouts.new_partner_landing.fields"
          dataTestId={newPartnerLandingFormTestIds.formField('viewBenefits')}
        />
        <LokaliseField
          name="benefitsList"
          control={control}
          toTranslate="layouts.new_partner_landing.fields"
          dataTestId={newPartnerLandingFormTestIds.formField('benefitsList')}
        />
        <LokaliseField
          name="hideBenefits"
          control={control}
          toTranslate="layouts.new_partner_landing.fields"
          dataTestId={newPartnerLandingFormTestIds.formField('hideBenefits')}
        />
        <LokaliseField
          name="legalAdsNotification"
          control={control}
          toTranslate="layouts.new_partner_landing.fields"
          dataTestId={newPartnerLandingFormTestIds.formField('legalAdsNotification')}
        />
        <LokaliseField
          name="selectPlanCta"
          control={control}
          toTranslate="layouts.new_partner_landing.fields"
          dataTestId={newPartnerLandingFormTestIds.formField('selectPlanCta')}
          required
        />
      </Box>
      <Box>
        <SectionHeader>{t('layouts.new_partner_landing.headers.footer')}</SectionHeader>
        <LokaliseField
          name="legalDisclaimer"
          control={control}
          toTranslate="layouts.new_partner_landing.fields"
          dataTestId={newPartnerLandingFormTestIds.formField('legalDisclaimer')}
          required
        />
      </Box>
      <Box>
        <SectionHeader>{t('layouts.new_partner_landing.headers.faq')}</SectionHeader>
        <LokaliseField
          name="faqTitle"
          control={control}
          toTranslate="layouts.new_partner_landing.fields"
          dataTestId={newPartnerLandingFormTestIds.formField('faqTitle')}
        />
        <div />
        <div>
          <Sortable
            list={fieldsFaqQuestions}
            setList={replaceFaqQuestionsMethod}
            ghostClass="sortableGhost"
            dragClass="sortableDragDefault"
          >
            {fieldsFaqQuestions.map((faq, i) => (
              <Stack
                key={faq.id}
                data-testid={newPartnerLandingFormTestIds.faqQuestions}
                gap={6}
                marginBottom={5}
                direction="row"
                alignItems="center"
              >
                {LokaliseFieldFaqQuestions('question', i)}
                {LokaliseFieldFaqQuestions('answer', i)}
                <IconButton
                  title={t('layouts.new_partner_landing.actions.remove_faq_questions')}
                  onClick={() => removeFaqQuestions(i)}
                  sx={{ mt: 4 }}
                  data-testid={newPartnerLandingFormTestIds.faqQuestionsRemoveButton}
                >
                  <Delete />
                </IconButton>
              </Stack>
            ))}
          </Sortable>
          <Button
            startIcon={<Add />}
            color={formControlColor}
            variant="outlined"
            onClick={onAddFaqQuestions}
            data-testid={newPartnerLandingFormTestIds.faqQuestionsAddButton}
          >
            {t('layouts.new_partner_landing.actions.add_faq_questions')}
          </Button>
        </div>
      </Box>
    </FormBody>
  );
});

NewPartnerLandingForm.displayName = 'NewPartnerLandingForm';

export default NewPartnerLandingForm;
